import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { COUNTRIES } from "../lib/regions";

/**
 * Region picker — searchable combobox of every country alphabetically.
 * Typing filters by name OR alpha-2 code (e.g. "MY" → Malaysia).
 * Returns the human-readable value on selection so it persists in the existing region column.
 */
export default function RegionPicker({ value = "", onChange, placeholder = "Search country…", testId = "region-picker" }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    // Focus the search input when popover opens
    useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);

    const q = query.trim().toLowerCase();
    const filteredCountries = useMemo(
        () => COUNTRIES.filter((c) => !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q || c.code.toLowerCase().startsWith(q)),
        [q]
    );
    const total = filteredCountries.length;

    const choose = (label) => {
        onChange?.(label);
        setOpen(false);
        setQuery("");
    };

    return (
        <div className="relative" ref={wrapRef} data-testid={testId}>
            {/* Trigger button */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-left text-sm flex items-center justify-between gap-2 hover:bg-zinc-200/60"
                data-testid={`${testId}-trigger`}
            >
                <span className={value ? "text-zinc-900" : "text-zinc-400"}>{value || placeholder}</span>
                <div className="flex items-center gap-1">
                    {value && (
                        <span
                            role="button"
                            onClick={(e) => { e.stopPropagation(); choose(""); }}
                            className="p-0.5 rounded hover:bg-zinc-300 text-zinc-500"
                            data-testid={`${testId}-clear`}
                        ><X className="h-3.5 w-3.5" /></span>
                    )}
                    <ChevronDown className="h-4 w-4 text-zinc-500" />
                </div>
            </button>

            {/* Popover */}
            {open && (
                <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-zinc-200 rounded-md shadow-lg" data-testid={`${testId}-menu`}>
                    <div className="p-2 border-b border-zinc-100">
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Type to filter…"
                            className="w-full px-2 py-1.5 bg-zinc-100 border border-zinc-200 rounded text-sm"
                            data-testid={`${testId}-search`}
                        />
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1">
                        {total === 0 && (
                            <div className="px-3 py-4 text-center text-xs text-zinc-500">No match for "{query}"</div>
                        )}
                        {filteredCountries.map((c) => {
                            const selected = value === c.name;
                            return (
                                <button
                                    key={c.code}
                                    type="button"
                                    onClick={() => choose(c.name)}
                                    className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-zinc-50 ${selected ? "bg-zinc-50" : ""}`}
                                    data-testid={`${testId}-opt-${c.code}`}
                                >
                                    <span className="text-zinc-900">
                                        {c.name}
                                        <span className="text-[10px] text-zinc-400 font-mono ml-2">{c.code}</span>
                                    </span>
                                    {selected && <Check className="h-3.5 w-3.5 wl-accent-text" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
